"use client";

import { useState } from "react";
import Link from "next/link";
import type { Id } from "@convex/_generated/dataModel";
import { Loader2 } from "@/components/icons";
import type {
  BuyAgainItem,
  BuyAgainReview,
  BuyAgainUnavailableItem,
} from "@convex/schemas/validators/buyAgain.lib";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatCents } from "@/lib/formatting/currency";
import {
  BUY_AGAIN_REQUEST_HREF,
  buyAgainAlternativesHref,
  buyAgainDeliveryNote,
  buyAgainUnavailableReason,
} from "@/lib/orders/buy-again-copy";
import { BuyAgainCartChoices } from "./buy-again-cart-choices";

interface BuyAgainDialogProps {
  review: BuyAgainReview | null;
  isWorking: boolean;
  onContinue: (keepCartQuantityFor: Id<"products">[]) => void;
  onClose: () => void;
}

function UnavailableLine({ entry }: { entry: BuyAgainUnavailableItem }) {
  const alternatives = buyAgainAlternativesHref(entry);
  return (
    <li className="py-2">
      <p className="text-sm font-medium">{entry.productName}</p>
      <p className="text-sm text-muted-foreground">
        {buyAgainUnavailableReason(entry)}
      </p>
      {alternatives ? (
        <Link href={alternatives} className="text-sm text-link hover:underline">
          See similar listings
        </Link>
      ) : null}
    </li>
  );
}

function priceChanged(item: BuyAgainItem): boolean {
  return (
    item.previousUnitPriceCents !== undefined &&
    item.previousUnitPriceCents !== item.unitPriceCents
  );
}

function ItemChanges({ item }: { item: BuyAgainItem }) {
  return priceChanged(item) && item.previousUnitPriceCents !== undefined ? (
    <span className="block text-muted-foreground">
      Last time {formatCents(item.previousUnitPriceCents)} each.
    </span>
  ) : null;
}

function AvailableLine({ item }: { item: BuyAgainItem }) {
  return (
    <li className="flex items-baseline justify-between gap-3 py-2">
      <span className="text-sm">
        <span className="font-medium">{item.productName}</span>
        <span className="block tabular-nums text-muted-foreground">
          {item.quantity.toLocaleString()} × {formatCents(item.unitPriceCents)}
        </span>
        <ItemChanges item={item} />
      </span>
      <span className="shrink-0 text-sm font-medium tabular-nums">
        {formatCents(item.lineTotalCents)}
      </span>
    </li>
  );
}

function ReviewLines({ review }: { review: BuyAgainReview }) {
  return (
    <div className="space-y-4">
      {review.items.length > 0 ? (
        <section>
          <h3 className="text-overline uppercase text-muted-foreground">
            Ready to order
          </h3>
          <ul className="divide-y">
            {review.items.map((item) => (
              <AvailableLine key={item.productId} item={item} />
            ))}
          </ul>
        </section>
      ) : null}
      {review.unavailable.length > 0 ? (
        <section>
          <h3 className="text-overline uppercase text-muted-foreground">
            Not available
          </h3>
          <ul className="divide-y">
            {review.unavailable.map((entry) => (
              <UnavailableLine
                key={entry.productId ?? entry.productName}
                entry={entry}
              />
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

function useCartChoices() {
  const [keepCart, setKeepCart] = useState<ReadonlySet<Id<"products">>>(
    () => new Set(),
  );
  const chooseCart = (productId: Id<"products">, keep: boolean) =>
    setKeepCart((current) => {
      const next = new Set(current);
      if (keep) next.add(productId);
      else next.delete(productId);
      return next;
    });
  return { keepCart, chooseCart };
}

function ReviewAction({
  canContinue,
  isWorking,
  onContinue,
}: {
  canContinue: boolean;
  isWorking: boolean;
  onContinue: () => void;
}) {
  if (!canContinue)
    return (
      <Button variant="primary" asChild>
        <Link href={BUY_AGAIN_REQUEST_HREF}>Request a quote</Link>
      </Button>
    );
  return (
    <Button variant="primary" onClick={onContinue} disabled={isWorking}>
      {isWorking ? <Loader2 className="size-4 animate-spin" /> : null}
      Continue to checkout
    </Button>
  );
}

export function BuyAgainDialog({
  review,
  isWorking,
  onContinue,
  onClose,
}: BuyAgainDialogProps) {
  const canContinue = (review?.items.length ?? 0) > 0;
  const { keepCart, chooseCart } = useCartChoices();
  return (
    <Dialog open={review !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Buy again from {review?.orderNumber}</DialogTitle>
          <DialogDescription>
            {canContinue && review
              ? buyAgainDeliveryNote(review)
              : "Nothing from this order can be bought online right now."}
          </DialogDescription>
        </DialogHeader>
        {review ? <ReviewLines review={review} /> : null}
        {review && canContinue ? (
          <BuyAgainCartChoices
            review={review}
            keepCart={keepCart}
            onChange={chooseCart}
          />
        ) : null}
        <DialogFooter>
          <ReviewAction
            canContinue={canContinue}
            isWorking={isWorking}
            onContinue={() => onContinue([...keepCart])}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
